import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useChartContext, useFormDataContext } from '~/hooks';
import { ChartData } from '~/types';

export const useIncomePot = (potIndex: number) => {
    const { pathname } = useLocation();
    const { data } = useChartContext();
    const {
        targetIncome: { amount: targetIncomeAmount },
    } = useFormDataContext();

    const [targetStatus, setTargetStatus] = useState<'TARGET_EXCEEDED' | 'TARGET_REACHED' | 'TARGET_NOT_REACHED'>(
        'TARGET_NOT_REACHED'
    );

    const chartData = data[potIndex];

    const totalIncome = useMemo(() => chartData.reduce((acc, item) => acc + Number(item.y), 0), [chartData]);

    const maxValue = Math.floor(Math.max(targetIncomeAmount, totalIncome) * 1.6);
    const previousData = useRef<ChartData[]>([]);
    const animationRefs = useRef<{ [key: string]: { height: SVGAnimateElement; y: SVGAnimateElement } }>({});

    const bars = useMemo(() => {
        const filteredData = chartData.filter(item => item.y >= 0);
        const result = [];
        let accumulatedHeight = 0;

        for (const item of filteredData) {
            const barHeight = (item.y / maxValue) * 167;
            const expectedPath = `/${item.label.toLowerCase().replace(' ', '-')}`;
            const isCurrentRoute = pathname === expectedPath;
            const shouldAnimate = isCurrentRoute;
            const y = 167 - accumulatedHeight - barHeight;

            result.push({
                ...item,
                barHeight,
                shouldAnimate,
                y,
                accumulatedHeight,
            });
            accumulatedHeight += barHeight;
        }

        previousData.current = filteredData;
        return result;
    }, [chartData, maxValue, pathname]);

    useEffect(() => {
        const timeout = setTimeout(() => {
            bars.forEach(bar => {
                if (bar.shouldAnimate && animationRefs.current[bar.label]) {
                    const { height, y } = animationRefs.current[bar.label];
                    height.beginElement();
                    y.beginElement();
                }
            });
        }, 100);

        return () => clearTimeout(timeout);
    }, [bars]);

    const incomeWithoutAnnuity = useMemo(
        () => chartData.filter(item => item.label !== 'Annuity').reduce((acc, item) => acc + Number(item.y), 0),
        [chartData]
    );

    useEffect(() => {
        if (incomeWithoutAnnuity >= targetIncomeAmount) {
            setTimeout(() => setTargetStatus('TARGET_EXCEEDED'), 700);
        } else if (totalIncome >= targetIncomeAmount) {
            setTimeout(() => setTargetStatus('TARGET_REACHED'), 700);
        } else {
            setTimeout(() => setTargetStatus('TARGET_NOT_REACHED'), 700);
        }
    }, [targetIncomeAmount, totalIncome, incomeWithoutAnnuity]);

    return {
        animationRefs,
        bars,
        data: chartData,
        maxValue,
        targetIncome: targetIncomeAmount,
        targetMet: targetStatus !== 'TARGET_NOT_REACHED',
        totalIncome,
        targetStatus,
    };
};
